"use client";

import * as AvatarPrimitive from "@radix-ui/react-avatar";
import * as React from "react";
import { cn } from "./lib/utils";

export interface AvatarProps {
	imageSrc?: string | null;
	name?: string | null;
	formatter?: (value?: string | null) => string;
	className?: string;
}

function extractInitials(name?: string | null): string {
	// Handle empty or undefined cases
	if (!name) return "";

	// Remove extra spaces and split on spaces, dots, and special characters
	const parts = name
		.replace(/[^a-zA-Z\s.@]/g, " ")
		.split(/[\s.@]+/)
		.filter(Boolean);

	if (parts.length === 0) return "";

	// If only one part, take first character
	if (parts.length === 1) {
		return parts[0].charAt(0).toUpperCase();
	}

	// Take first character of first and last parts
	return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
}

function Avatar({
	imageSrc,
	name,
	formatter = extractInitials,
	className,
}: AvatarProps): React.ReactElement {
	return (
		<AvatarRoot className={className}>
			{imageSrc ? (
				<AvatarImage src={imageSrc} alt={name ?? "Profile photo"} />
			) : null}
			<AvatarFallback>{formatter(name ?? "Unknown")}</AvatarFallback>
		</AvatarRoot>
	);
}
Avatar.displayName = "Avatar";

const AvatarRoot = React.forwardRef<
	React.ElementRef<typeof AvatarPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
	<AvatarPrimitive.Root
		ref={ref}
		className={cn(
			"relative flex h-8 w-8 shrink-0 overflow-hidden rounded-lg",
			className,
		)}
		{...props}
	/>
));
AvatarRoot.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
	React.ElementRef<typeof AvatarPrimitive.Image>,
	React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
	<AvatarPrimitive.Image
		ref={ref}
		className={cn("aspect-square h-full w-full", className)}
		{...props}
	/>
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
	React.ElementRef<typeof AvatarPrimitive.Fallback>,
	React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
	<AvatarPrimitive.Fallback
		ref={ref}
		className={cn(
			"flex h-full w-full items-center justify-center bg-jace text-text-body-inverted font-bold",
			className,
		)}
		{...props}
	/>
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarFallback, AvatarImage, AvatarRoot };
